import { growthAxes } from "../../lib/growth-chart";
import { asDate, istRangeStart, istDateKey, istISOString, formatCalendarDate } from "../../lib/admin-time";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Line } from "react-chartjs-2";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import DateDisplay from "../../components/ui-kit/date-display";
import { PulseLoader } from "react-spinners";
import "chartjs-adapter-date-fns";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  Legend,
  Tooltip,
  Filler,
} from "chart.js";
import {
  Globe,
  TrendingUp,
  Search,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  ArrowUpDown,
  Download,
  Users as UsersIcon,
  CheckCircle2,
  BarChart3,
  Layers,
  Database,
} from "lucide-react";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  Legend,
  Tooltip,
  Filler
);

const parseProjectDate = asDate;

export default function Projects() {
  const { getProjectData, getAnalyticsData } = useContext(APIContext);
  const [projectList, setProjectList] = useState([]);
  const [projectDataAnalytics, setProjectDataAnalytics] = useState([]);
  const [dateOffset, setDateOffset] = useState(30);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [sorting, setSorting] = useState([{ id: "created_on", desc: true }]);

  const getStartDate = istRangeStart;

  useEffect(() => {
    setLoadingProjects(true);
    getProjectData()
      .then((data) => {
        setProjectList(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error("Failed to load projects:", error);
      })
      .finally(() => {
        setLoadingProjects(false);
      });
  }, []);

  useEffect(() => {
    setLoadingAnalytics(true);
    const dateString = getStartDate(dateOffset);
    getAnalyticsData(dateString)
      .then((projectdata) => {
        setProjectDataAnalytics(Array.isArray(projectdata) ? projectdata : []);
      })
      .catch((error) => {
        console.error("Failed to load analytics data:", error);
      })
      .finally(() => {
        setLoadingAnalytics(false);
      });
  }, [dateOffset]);

  const lifetimeProjects = useMemo(
    () => projectDataAnalytics?.map((item) => item.lifetime_projects) || [],
    [projectDataAnalytics]
  );
  const newProjects = useMemo(
    () => projectDataAnalytics?.map((item) => item.new_projects) || [],
    [projectDataAnalytics]
  );
  const chartLabels = useMemo(
    () => projectDataAnalytics?.map((item) => formatCalendarDate(item.sk)) || [],
    [projectDataAnalytics]
  );

  const latestLifetime = useMemo(() => {
    if (!projectDataAnalytics || projectDataAnalytics.length === 0) return 0;
    return (
      projectDataAnalytics[projectDataAnalytics.length - 1]?.lifetime_projects ||
      0
    );
  }, [projectDataAnalytics]);

  const totalNewInPeriod = useMemo(() => {
    if (!projectDataAnalytics || projectDataAnalytics.length === 0) return 0;
    return projectDataAnalytics
      .map((item) => item.new_projects)
      .reduce((a, b) => parseInt(a ?? 0, 10) + parseInt(b ?? 0, 10), 0);
  }, [projectDataAnalytics]);

  const totalPublishedCount = useMemo(() => {
    return projectList.filter((p) => {
      const s = (p.status || "").toLowerCase();
      return s === "published" || Boolean(p.last_published);
    }).length;
  }, [projectList]);

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Total Websites",
        data: lifetimeProjects,
        spanGaps: true,
        fill: false,
        backgroundColor: "#623ce7",
        borderColor: "#623ce7",
        borderWidth: 2.5,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: "New Websites (right axis)",
        yAxisID: "daily",
        data: newProjects,
        spanGaps: true,
        fill: false,
        backgroundColor: "#10b981",
        borderColor: "#10b981",
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          font: {
            family: "Inter, sans-serif",
            size: 12,
          },
          color: "#475569",
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#0f172a",
        titleFont: { size: 12, family: "Inter, sans-serif" },
        bodyFont: { size: 12, family: "Inter, sans-serif" },
        padding: 10,
        cornerRadius: 8,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    scales: {
      x: {
        type: "category",
        parsing: false,
        time: {
          unit: "day",
          tooltipFormat: "PP",
        },
        grid: {
          display: false,
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
          maxRotation: 0,
        },
      },
      ...growthAxes("Total websites", "New websites / day"),
    },
  };

  // Filter projects by status and global query
  const filteredProjects = useMemo(() => {
    return projectList.filter((project) => {
      // Status filter
      const isPub =
        (project.status || "").toLowerCase() === "published" ||
        Boolean(project.last_published);

      if (statusFilter === "published" && !isPub) return false;
      if (statusFilter === "unpublished" && isPub) return false;

      // Global search filter
      const q = globalFilter.toLowerCase().trim();
      if (!q) return true;

      const projectID = String(
        project.id || project.pk?.replace("project-", "") || ""
      ).toLowerCase();
      const name = String(project.name || "").toLowerCase();
      const slug = String(project.slug || "").toLowerCase();
      const domain = String(project.domain || "").toLowerCase();

      return (
        name.includes(q) ||
        projectID.includes(q) ||
        slug.includes(q) ||
        domain.includes(q)
      );
    });
  }, [projectList, statusFilter, globalFilter]);

  const columns = useMemo(
    () => [
      {
        id: "website",
        header: "Website",
        accessorFn: (row) => row.name || "Untitled",
        cell: ({ row }) => {
          const project = row.original;
          const projectID =
            project.id || project.pk?.replace("project-", "") || "";
          const logoUrl = project.sitelogo
            ? `https://prod.grigora-cdn.com/projects/${projectID}/${project.sitelogo}`
            : project.favicon
            ? `https://prod.grigora-cdn.com/projects/${projectID}/${project.favicon}`
            : "https://prod.grigora-cdn.com/defaults/img/webclip.png";

          return (
            <div className="flex items-center gap-3 min-w-[220px]">
              <img
                src={logoUrl}
                alt={project.name || "site"}
                className="w-9 h-9 rounded-lg object-contain border border-slate-200 bg-slate-50 p-1 shrink-0"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://prod.grigora-cdn.com/defaults/img/webclip.png";
                }}
              />
              <div className="min-w-0">
                <Link target="_blank" rel="noopener noreferrer"
                  to={`/projects/${projectID}`}
                  className="font-semibold text-slate-900 hover:text-[#623ce7] transition-colors block truncate text-sm"
                  title={project.name || "Untitled Website"}
                >
                  {project.name || "Untitled Website"}
                </Link>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-[11px] font-mono text-slate-400 truncate">
                    ID: {projectID}
                  </span>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: "domain_slug",
        header: "Slug / Domain",
        accessorFn: (row) => row.domain || row.slug || "",
        cell: ({ row }) => {
          const project = row.original;
          const liveUrl = project.domain
            ? `https://${project.domain}`
            : project.slug
            ? `https://${project.slug}.grigora.app`
            : null;

          return (
            <div className="space-y-1 min-w-[180px]">
              {project.slug && (
                <div className="flex items-center gap-1.5 text-xs text-slate-700">
                  <span className="font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[11px] truncate max-w-[180px]">
                    {project.slug}.grigora.app
                  </span>
                  {liveUrl && (
                    <a
                      href={liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-400 hover:text-[#623ce7] transition-colors shrink-0"
                      title="Visit live site"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              )}
              {project.domain ? (
                <div className="flex items-center gap-1.5 text-xs text-slate-900 font-medium">
                  <Globe size={12} className="text-emerald-500 shrink-0" />
                  <a
                    href={`https://${project.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline hover:text-[#623ce7] truncate max-w-[180px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {project.domain}
                  </a>
                </div>
              ) : (
                <span className="text-[11px] text-slate-400 italic">
                  No custom domain
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) =>
          (row.status || "").toLowerCase() === "published" ||
          Boolean(row.last_published)
            ? "Published"
            : "Unpublished",
        cell: ({ row }) => {
          const project = row.original;
          const isPub =
            (project.status || "").toLowerCase() === "published" ||
            Boolean(project.last_published);

          return (
            <div>
              {isPub ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Published
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                  Unpublished
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "created_on",
        header: "Created Date",
        accessorFn: (row) => {
          const d = parseProjectDate(row.created_on);
          return d ? d.getTime() : 0;
        },
        cell: ({ row }) => {
          const d = parseProjectDate(row.original.created_on);
          return (
            <div className="text-xs text-slate-600">
              {d ? (
                <DateDisplay date={d} />
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </div>
          );
        },
      },
      {
        id: "members",
        header: "Members",
        accessorFn: (row) => {
          if (!row.members) return 0;
          return Array.isArray(row.members)
            ? row.members.length
            : Object.keys(row.members).length;
        },
        cell: ({ row }) => {
          const count = row.getValue("members");
          return (
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <UsersIcon size={14} className="text-slate-400" />
              <span className="font-semibold text-slate-800">{count}</span>
              <span className="text-slate-400">
                {count === 1 ? "member" : "members"}
              </span>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Quick Actions",
        enableSorting: false,
        cell: ({ row }) => {
          const project = row.original;
          const projectID =
            project.id || project.pk?.replace("project-", "") || "";

          return (
            <div
              className="flex items-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <Link target="_blank" rel="noopener noreferrer"
                to={`/projects/${projectID}`}
                className="px-2.5 py-1 text-xs font-medium text-[#623ce7] bg-[#623ce7]/10 hover:bg-[#623ce7]/20 rounded-md transition-colors"
                title="Manage Website"
              >
                Manage
              </Link>
              <Link target="_blank" rel="noopener noreferrer"
                to={`/projects/${projectID}/pages`}
                className="px-2.5 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                title="View Website Pages"
              >
                Pages
              </Link>
              <Link target="_blank" rel="noopener noreferrer"
                to={`/projects/${projectID}/cms`}
                className="px-2.5 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                title="Manage Website CMS"
              >
                CMS
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: filteredProjects,
    columns,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const filteredTotal = filteredProjects.length;

  const downloadTableData = () => {
    const csvHeaders = [
      "Project ID",
      "Name",
      "Slug",
      "Custom Domain",
      "Status",
      "Created Date",
      "Members Count",
      "Live URL",
    ].join(",");

    const csvRows = filteredProjects.map((p) => {
      const projectID = p.id || p.pk?.replace("project-", "") || "";
      const isPub =
        (p.status || "").toLowerCase() === "published" ||
        Boolean(p.last_published);
      const d = parseProjectDate(p.created_on);
      const dateStr = d ? istISOString(d) : "";
      const memberCount = p.members
        ? Array.isArray(p.members)
          ? p.members.length
          : Object.keys(p.members).length
        : 0;
      const liveUrl = p.domain
        ? `https://${p.domain}`
        : p.slug
        ? `https://${p.slug}.grigora.app`
        : "";

      const values = [
        `"${projectID}"`,
        `"${(p.name || "").replace(/"/g, '""')}"`,
        `"${(p.slug || "").replace(/"/g, '""')}"`,
        `"${(p.domain || "").replace(/"/g, '""')}"`,
        `"${isPub ? "Published" : "Unpublished"}"`,
        `"${dateStr}"`,
        memberCount,
        `"${liveUrl}"`,
      ];
      return values.join(",");
    });

    const csvContent = `${csvHeaders}\n${csvRows.join("\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `websites_export_${istDateKey()}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
  };

  const currentSortKey = `${sorting[0]?.id || "created_on"}-${
    sorting[0]?.desc ? "desc" : "asc"
  }`;

  return (
    <>
      <HelmetLayout title="Websites" />
      <div className="space-y-6 pb-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Websites</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Monitor registered customer websites, published sites, and creation analytics.
            </p>
          </div>
          <button
            onClick={downloadTableData}
            disabled={filteredProjects.length === 0}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={15} />
            Export CSV
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">
                Total Lifetime Websites
              </p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loadingAnalytics ? "—" : latestLifetime.toLocaleString()}
              </h3>
            </div>
            <div className="p-3 bg-[#623ce7]/10 text-[#623ce7] rounded-xl">
              <Globe size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">
                New Websites ({dateOffset}d)
              </p>
              <h3 className="text-2xl font-bold text-emerald-600">
                {loadingAnalytics ? "—" : totalNewInPeriod.toLocaleString()}
              </h3>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <TrendingUp size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">
                Published Websites
              </p>
              <h3 className="text-2xl font-bold text-blue-600">
                {loadingProjects ? "—" : totalPublishedCount.toLocaleString()}
              </h3>
            </div>
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <CheckCircle2 size={22} />
            </div>
          </div>
        </div>

        {/* Chart Section */}
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h3 className="text-base font-bold text-slate-900">
                Websites Growth
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Trend of lifetime website count and newly deployed websites.
              </p>
            </div>

            {/* Date Range Selector Pills */}
            <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-lg">
              {[7, 15, 30, 60].map((days) => (
                <button
                  key={days}
                  onClick={() => setDateOffset(days)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    dateOffset === days
                      ? "bg-white text-slate-900 shadow-sm font-semibold"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>

          <div className="w-full h-80">
            {loadingAnalytics ? (
              <div className="h-full flex items-center justify-center">
                <PulseLoader size={10} color="#623ce7" />
              </div>
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
        </div>

        {/* Tabular Websites List Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Controls Bar */}
          <div className="p-4 border-b border-slate-200 flex flex-col gap-4 bg-slate-50/50">
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
              {/* Search Input */}
              <div className="relative flex-1 max-w-md">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={17}
                />
                <input
                  type="text"
                  placeholder="Search by website name, ID, slug, or domain..."
                  value={globalFilter}
                  onChange={(e) => {
                    setGlobalFilter(e.target.value);
                    table.setPageIndex(0);
                  }}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
                />
              </div>

              {/* Quick Sort Dropdown */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-semibold whitespace-nowrap flex items-center gap-1">
                  <ArrowUpDown size={14} /> Sort:
                </span>
                <select
                  className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 outline-none"
                  value={currentSortKey}
                  onChange={(e) => {
                    const [id, dir] = e.target.value.split("-");
                    setSorting([{ id, desc: dir === "desc" }]);
                    table.setPageIndex(0);
                  }}
                >
                  <option value="created_on-desc">Created: Newest First</option>
                  <option value="created_on-asc">Created: Oldest First</option>
                  <option value="website-asc">Name: A to Z</option>
                  <option value="website-desc">Name: Z to A</option>
                  <option value="status-asc">Status: Published First</option>
                  <option value="status-desc">Status: Unpublished First</option>
                  <option value="members-desc">Members: Most First</option>
                  <option value="members-asc">Members: Fewest First</option>
                </select>
              </div>
            </div>

            {/* Filters and Rows Per Page */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                {/* Status Filter */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-slate-500 font-medium">Status:</span>
                  <select
                    className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 outline-none"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      table.setPageIndex(0);
                    }}
                  >
                    <option value="all">All Websites ({projectList.length})</option>
                    <option value="published">
                      Published ({totalPublishedCount})
                    </option>
                    <option value="unpublished">
                      Unpublished ({projectList.length - totalPublishedCount})
                    </option>
                  </select>
                </div>
              </div>

              {/* Rows Per Page */}
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-xs text-slate-500 font-medium">Rows:</span>
                <select
                  className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-slate-700 outline-none"
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(parseInt(e.target.value, 10));
                    table.setPageIndex(0);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200 select-none"
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className="p-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-1.5">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <span className="text-[10px] text-slate-400">
                            {header.column.getIsSorted() === "asc"
                              ? "▲"
                              : header.column.getIsSorted() === "desc"
                              ? "▼"
                              : ""}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {loadingProjects ? (
                  <tr>
                    <td colSpan={columns.length} className="p-12 text-center">
                      <PulseLoader color="#623ce7" size={10} />
                      <p className="text-xs text-slate-400 mt-2">
                        Loading websites...
                      </p>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="p-12 text-center text-slate-400"
                    >
                      No websites match the selected criteria.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    const projectID =
                      row.original.id ||
                      row.original.pk?.replace("project-", "") ||
                      "";
                    return (
                      <tr
                        key={row.id}
                        className="hover:bg-slate-50/80 transition-colors cursor-pointer"

                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="p-4 whitespace-nowrap">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="p-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-50/30 text-xs text-slate-500">
            <div>
              Showing{" "}
              <strong className="text-slate-700 font-semibold">
                {filteredTotal === 0
                  ? 0
                  : pagination.pageIndex * pagination.pageSize + 1}
              </strong>{" "}
              to{" "}
              <strong className="text-slate-700 font-semibold">
                {Math.min(
                  (pagination.pageIndex + 1) * pagination.pageSize,
                  filteredTotal
                )}
              </strong>{" "}
              of{" "}
              <strong className="text-slate-700 font-semibold">
                {filteredTotal}
              </strong>{" "}
              websites
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-slate-600 font-medium px-2">
                Page {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount() || 1}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
